import Axios from "axios";
import {
  applyAuthTokenInterceptor,
  clearAuthTokens,
  getAccessToken,
  IAuthTokens,
  isLoggedIn as isloggedin,
  setAuthTokens,
  TokenRefreshRequest,
} from "axios-jwt";
import jwtDecode from "jwt-decode";

export const LOGIN_PATH = "/avc/login0";

export interface PanelAdmin {
  id?: string;
  adminID?: string;
  username: string;
  name?: string;
  email?: string;
  role: "admin" | "manager";
  permissions: string[];
  tokenType?: "admin";
}

let currentAdmin: PanelAdmin | null = null;

export class SessionExpiredError extends Error {
  constructor() {
    super("session_expired");
    this.name = "SessionExpiredError";
  }
}

export function isSessionExpiredError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    error instanceof SessionExpiredError ||
    message.includes("session_expired") ||
    message.includes("refresh auth token") ||
    message.includes("Got 401 on token refresh") ||
    message.includes("Got 422 on token refresh")
  );
}

export function redirectToLogin(): void {
  currentAdmin = null;
  clearAuthTokens();
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
}

export const getBaseURL = () => {
  const explicit = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
  if (explicit) {
    return explicit;
  }
  if (process.env.NODE_ENV === "development") {
    const apiPort = process.env.REACT_APP_API_PORT || "3090";
    const host =
      typeof window !== "undefined" && window.location.hostname
        ? window.location.hostname
        : "localhost";
    return `http://${host}:${apiPort}`;
  }
  return undefined;
};

/** Same origin the `Api` instance uses (see axios-jwt README: refresh must not use the intercepted instance, but must hit the same API base). */
const getRefreshTokenUrl = () => {
  const base = getBaseURL();
  const path = "/api/admins/refresh-token";
  if (!base) {
    return path;
  }
  return `${base.replace(/\/$/, "")}${path}`;
};

export const Api = Axios.create({
  baseURL: getBaseURL(),
});
Api.defaults.headers.common["rso"] = new Date().toString();

export function getUser(): PanelAdmin | null {
  if (currentAdmin) return currentAdmin;
  const token = getAccessToken();
  if (token) {
    return jwtDecode<PanelAdmin>(token);
  }
  return null;
}

export async function getCurrentAdmin(): Promise<PanelAdmin> {
  const response = await Api.get("/api/admin/panel/me");
  currentAdmin = response.data.data as PanelAdmin;
  return currentAdmin;
}

export const requestRefresh: TokenRefreshRequest = async (
  refreshToken: string,
): Promise<IAuthTokens | string> => {
  const token = String(refreshToken ?? "").trim();
  if (!token) {
    clearAuthTokens();
    redirectToLogin();
    throw new SessionExpiredError();
  }

  try {
    const response = await Axios.post(
      getRefreshTokenUrl(),
      { refreshToken: token },
      {
        headers: {
          rso: Api.defaults.headers.common["rso"],
        },
      },
    );
    const payload = response?.data;
    if (payload?.status === "not_found") {
      clearAuthTokens();
      throw new Error("Token refresh failed: admin no longer exists");
    }
    const tokens = payload?.data;
    if (tokens?.accessToken) {
      return tokens;
    }
    clearAuthTokens();
    throw new Error(
      `Token refresh failed: unexpected response ${JSON.stringify(payload ?? null).slice(0, 200)}`,
    );
  } catch (error: unknown) {
    clearAuthTokens();
    redirectToLogin();
    throw new SessionExpiredError();
  }
};
applyAuthTokenInterceptor(Api, { requestRefresh });

Api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const responseStatus = error?.response?.data?.status;
    if (status === 401 || responseStatus === "session_expired") {
      redirectToLogin();
    }
    return Promise.reject(error);
  },
);
interface ILoginRequest {
  username: string;
  password: string;
}
export const login = async (
  params: ILoginRequest,
): Promise<"successful" | "notExists" | "disabled" | "wrongPassword"> => {
  const response = await Api.post("/api/admins/login", params);

  if (response.data.status === "successful") {
    setAuthTokens({
      accessToken: response.data.data.accessToken,
      refreshToken: response.data.data.refreshToken,
    });
    currentAdmin = null;

    return response.data.status;
  } else {
    clearAuthTokens();
    return response.data.status;
  }
};
export const logout = () => {
  currentAdmin = null;
  clearAuthTokens();
};
export const isLoggedIn = isloggedin;
